import { useCallback } from 'react'
import { entityAPI } from '../services/api'
import { toast } from '../services/toastService'
import { createLogger } from '../utils/logger'
import { useTranslation } from 'react-i18next'

const logger = createLogger('useEntityLocations')

/**
 * Custom hook for fetching entity locations (sightings)
 * Handles fetching sightings for a specific entity with cache control
 * 
 * @param {Object} params - Configuration parameters
 * @param {string} params.entityId - The entity ID
 * @param {string} params.entityName - The entity name
 * @param {Function} params.setIsLoading - Loading state setter
 * @param {Function} params.setEntities - Entities state setter
 * @returns {Object} - { fetchEntityLocations }
 */
export function useEntityLocations({ entityId, entityName, setIsLoading, setEntities }) {
  const { t } = useTranslation()
  const fetchEntityLocations = useCallback(async (
    passedEntityId = null,
    centerLocation = null,
    passedEntityName = null,
    forceFresh = false
  ) => {
    const idToUse = passedEntityId || entityId
    const nameToUse = passedEntityName || entityName || 'Food Truck'

    if (!idToUse || idToUse === 'null') {
      logger.warn('fetchEntityLocations called with no valid entity ID')
      return
    }

    setIsLoading(true)
    try {
      const params = { 
        page: 1, 
        limit: 100
      }
      
      // Only add cache-buster when explicitly forcing fresh data (e.g., after confirmation)
      if (forceFresh) {
        params._t = Date.now()
      }
      
      // Configure axios to bypass cache when forceFresh is true
      const config = forceFresh ? {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      } : { params }
      
      const response = await entityAPI.getEntityLocations(idToUse, config.params, config.headers)
      const locations = response.data.data || []

      // Transform sightings into entity-like objects for markers
      const entityLocations = locations.map((sighting, index) => {
        const markerData = {
          id: sighting.id,
          userId: sighting.userId || sighting.user_id, // Include user ID for ownership check
          lastConfirmerId: sighting.lastConfirmerId || sighting.last_confirmer_id, // Include last confirmer ID
          entityId: idToUse, // Ensure entityId is always set for entity mode
          name: nameToUse,
          description: sighting.notes || 'Sighting location',
          currentLatitude: sighting.latitude,
          currentLongitude: sighting.longitude,
          currentAddress: sighting.address,
          confirmationCount: sighting.confirmationCount || sighting.confirmations || 0,
          photoUrl: sighting.photoUrl || sighting.photo_url,
          createdAt: sighting.createdAt || sighting.created_at,
          userName: sighting.userName || sighting.user?.name || 'Unknown',
          userAvatar: sighting.avatarUrl || sighting.avatar_url || sighting.user?.avatar_url,
          isLatest: index === 0
        }
        return markerData
      })

      setEntities(entityLocations)

      // If a specific center location is provided, use it; otherwise use first sighting
      if (centerLocation) {
        // Center location explicitly provided (e.g., after spot submission)
        // No need to do anything special - let the map center on the provided location
      } else if (entityLocations.length > 0) {
        // Center on first (most recent) sighting
        // This is handled by the mapCenter useMemo in the component
      }
    } catch (error) {
      logger.error('Failed to fetch entity locations', error)
      toast.error(t('toast.error.locationLoadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [entityId, entityName, setIsLoading, setEntities])

  return { fetchEntityLocations }
}
